/**
 * serverTime.ts — Module-level singleton for server time synchronization.
 *
 * ARCHITECTURE:
 *   - All pages/components share the SAME offset variable.
 *   - Whoever syncs first (evento page, home page, etc.) benefits everyone.
 *   - No duplication of NTP logic across components.
 *
 * USAGE:
 *   import { getNow, startAutoSync, setOffset } from '../lib/serverTime';
 *
 *   getNow()         → corrected epoch ms  (use instead of Date.now())
 *   startAutoSync()  → starts interval, returns cleanup fn
 *   setOffset(ms)    → allows high-precision callers (evento page) to share result
 */

import { supabase, isSupabaseConfigured } from './supabase';

// ─── Module state ──────────────────────────────────────────────────────────
let _offset = 0;       // ms: serverTime − localMidpoint
let _initialized = false;

// ─── Public API ────────────────────────────────────────────────────────────

/** Returns corrected "now" in milliseconds (server-synchronized). */
export function getNow(): number {
  return Date.now() + _offset;
}

/** Returns the current offset in ms. */
export function getOffset(): number {
  return _offset;
}

/**
 * Calculates milliseconds remaining to a target UTC timestamp.
 * Always uses server-corrected time — never raw device clock.
 *
 * RULE 4: Single countdown engine — use this everywhere instead of
 *   targetMs - Date.now()
 */
export function calculateRemaining(targetMs: number): number {
  return Math.max(0, targetMs - getNow());
}

/**
 * Directly overrides the offset.
 * Use this when a component has a higher-precision NTP result
 * (e.g., evento/page burst sampling) that it wants to share globally.
 */
export function setOffset(ms: number): void {
  _offset = ms;
  _initialized = true;
}

/**
 * Performs one NTP-style round-trip measurement.
 * Collects up to 3 samples and picks the one with lowest RTT.
 * Uses exponential smoothing to avoid abrupt jumps.
 */
export async function syncServerTime(): Promise<void> {
  if (!isSupabaseConfigured || !supabase) return;

  const samples: Array<{ offset: number; rtt: number }> = [];

  for (let i = 0; i < 3; i++) {
    try {
      const t0perf = performance.now();
      const t0wall = Date.now();
      const { data: serverTimeData, error } = await supabase.rpc('get_server_time');
      const rtt = performance.now() - t0perf; // sub-ms precision

      if (!error && serverTimeData) {
        const serverMs = new Date(serverTimeData as string).getTime();
        const localMidpoint = t0wall + rtt / 2;
        const offset = serverMs - localMidpoint;
        samples.push({ offset, rtt });
      }

      // Small gap between samples to reduce correlated jitter
      if (i < 2) await new Promise<void>((r) => setTimeout(r, 40));
    } catch {
      // silent — network may be flaky
    }
  }

  if (samples.length === 0) return;

  // Pick the sample with the lowest RTT (least network delay)
  const best = samples.reduce((a, b) => (a.rtt < b.rtt ? a : b));
  const newOffset = best.offset;

  // Exponential smoothing with adaptive weight:
  // - First sync: use directly (no history to blend with)
  // - Large delta (>500ms): trust new measurement more (80%) — device clock was drifted
  // - Small delta (<500ms): blend 60% new / 40% old — stable, avoids micro-jitter
  if (!_initialized) {
    _offset = newOffset;
  } else {
    const delta = Math.abs(newOffset - _offset);
    const weight = delta > 500 ? 0.80 : 0.60;
    _offset = _offset * (1 - weight) + newOffset * weight;
  }
  _initialized = true;

  const rtts = samples.map((s) => s.rtt.toFixed(1)).join('/');

  // ── RULE 8: Debug log — server_time, client_time, offset ────────────────
  const serverTime = new Date(best.offset + Date.now()).toISOString();
  const clientTime = new Date().toISOString();
  console.log(
    `[serverTime] server_time=${serverTime} client_time=${clientTime} offset=${_offset.toFixed(1)}ms RTTs=[${rtts}]ms`
  );
}

/**
 * Starts a burst sync on mount (3 rapid calls for fast convergence)
 * then syncs on a regular interval.
 *
 * Returns a cleanup function — call it on component unmount.
 *
 * Safe to call from multiple components; each registers its own interval
 * but they all write to the same shared _offset variable, so they converge
 * to the same value and reinforce accuracy rather than conflict.
 */
export function startAutoSync(intervalMs = 30_000): () => void {
  // Burst: immediate + 200ms + 700ms for fast initial convergence
  syncServerTime();
  const t1 = setTimeout(syncServerTime, 200);
  const t2 = setTimeout(syncServerTime, 700);
  const id = setInterval(syncServerTime, intervalMs);

  return () => {
    clearTimeout(t1);
    clearTimeout(t2);
    clearInterval(id);
  };
}
